import { useEffect, useRef, useState } from "react";
import { useGetCurrentUser } from "~/api/users/users";
import { getStoredAccessToken } from "~/helpers";
import { WebSocketClient } from "~/lib/wss-client";
import { WS_URL } from "~/utils/apiUrl";
import logger from "~/utils/logger";

export type PresentUser = {
  id: string;
  name: string;
  avatarUrl?: string | null;
};

type UseCasePresenceReturn = {
  presentUsers: PresentUser[];
  isConnected: boolean;
};

const PING_INTERVAL_MS = 15000;

export function useCasePresence(
  caseId: string | undefined
): UseCasePresenceReturn {
  const { data: currentUser } = useGetCurrentUser();
  const [presentUsers, setPresentUsers] = useState<PresentUser[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const wsClientRef = useRef<WebSocketClient | null>(null);
  const pingIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!caseId || !currentUser) {
      return;
    }

    const token = getStoredAccessToken();
    if (!token) {
      logger.error("No access token available for presence");
      return;
    }

    const sendPresenceUpdate = () => {
      try {
        wsClientRef.current?.sendMessage({
          messageType: "presence",
          action: "update",
          projectId: caseId,
          user: {
            id: currentUser.id,
            name: currentUser.name,
            avatarUrl: null,
          },
        });
      } catch (error) {
        logger.error("Error sending presence update:", error);
      }
    };

    let hasReceivedInitialBroadcast = false;

    const client = new WebSocketClient(
      WS_URL,
      {
        subscribeTo: "presence",
        projectId: caseId,
        token,
      },
      {
        tokenProvider: () => getStoredAccessToken(),
        onOpen: () => {
          setIsConnected(true);
        },
        onClose: () => {
          setIsConnected(false);
        },
      }
    );

    wsClientRef.current = client;

    const handlePresenceUpdate = (updates: PresentUser[]) => {
      const dedupMap = new Map<string, PresentUser>();
      for (const user of updates) {
        if (!dedupMap.has(user.id)) {
          dedupMap.set(user.id, user);
        }
      }
      setPresentUsers(Array.from(dedupMap.values()));

      if (!hasReceivedInitialBroadcast) {
        hasReceivedInitialBroadcast = true;
        sendPresenceUpdate();
      }
    };

    const handleError = (error: Event) => {
      logger.error("Presence WebSocket error:", error);
    };

    client.addListener(handlePresenceUpdate);
    client.addErrorListener(handleError);

    client.connect();

    const sendPing = () => {
      if (!hasReceivedInitialBroadcast) {
        return;
      }
      try {
        wsClientRef.current?.sendMessage({
          messageType: "presence",
          action: "ping",
          projectId: caseId,
        });
      } catch (error) {
        logger.error("Error sending presence ping:", error);
      }
    };

    pingIntervalRef.current = window.setInterval(sendPing, PING_INTERVAL_MS);

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      if (client.isConnected()) {
        try {
          client.sendMessage({
            messageType: "presence",
            action: "leave",
            projectId: caseId,
          });
        } catch (error) {
          logger.error("Error sending presence leave:", error);
        }
      }

      client.removeListener(handlePresenceUpdate);
      client.removeErrorListener(handleError);
      client.destroy();
      wsClientRef.current = null;
    };
  }, [caseId, currentUser]);

  return { presentUsers, isConnected };
}
